import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Wheat, Factory, Briefcase, Fuel, Gem, ArrowRight, Sparkles, Shirt, Cpu, Car, Home, Heart, Utensils, Laptop, Building } from 'lucide-react';

const getCategoriesData = (t) => [
  { 
    id: 'agricultural', 
    name: t('home.categories.agricultural'), 
    icon: Wheat, 
    gradient: 'from-emerald-500 to-green-600',
    iconBg: 'bg-emerald-500',
    description: t('home.categories.agriculturalDesc'),
    stats: t('home.categories.productsCount', { count: '50+' }),
    image: 'https://images.unsplash.com/photo-1595841696677-6489ff3f8cd1?w=600&h=400&fit=crop'
  },
  { 
    id: 'minerals', 
    name: t('home.categories.minerals'), 
    icon: Gem, 
    gradient: 'from-amber-500 to-yellow-600',
    iconBg: 'bg-amber-500',
    description: t('home.categories.mineralsDesc'),
    stats: t('home.categories.productsCount', { count: '25+' }),
    image: 'https://images.unsplash.com/photo-1610375461249-83b1d7b8c5c3?w=600&h=400&fit=crop'
  },
  { 
    id: 'furniture', 
    name: t('home.categories.furniture') || 'Furniture & Crafts', 
    icon: Home, 
    gradient: 'from-blue-500 to-indigo-600',
    iconBg: 'bg-blue-500',
    description: t('home.categories.furnitureDesc') || 'Handcrafted African furniture and home decor',
    stats: t('home.categories.productsCount', { count: '40+' }),
    image: 'https://images.pexels.com/photos/1350789/pexels-photo-1350789.jpeg?w=600&h=400&fit=crop'
  },
  { 
    id: 'oil_gas', 
    name: t('home.categories.oilGas'), 
    icon: Fuel, 
    gradient: 'from-slate-600 to-gray-700',
    iconBg: 'bg-slate-600',
    description: t('home.categories.oilGasDesc'),
    stats: t('home.categories.productsCount', { count: '15+' }),
    image: 'https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?w=600&h=400&fit=crop'
  },
  { 
    id: 'textiles', 
    name: t('home.categories.textiles'), 
    icon: Shirt, 
    gradient: 'from-pink-500 to-rose-600',
    iconBg: 'bg-pink-500',
    description: t('home.categories.textilesDesc'),
    stats: t('home.categories.productsCount', { count: '35+' }),
    image: 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=600&h=400&fit=crop'
  },
  { 
    id: 'electronics', 
    name: t('home.categories.electronics'), 
    icon: Cpu, 
    gradient: 'from-cyan-500 to-teal-600',
    iconBg: 'bg-cyan-500',
    description: t('home.categories.electronicsDesc'),
    stats: t('home.categories.productsCount', { count: '20+' }),
    image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&h=400&fit=crop'
  },
  { 
    id: 'food_beverages', 
    name: t('home.categories.foodBeverages'), 
    icon: Utensils, 
    gradient: 'from-orange-500 to-red-500',
    iconBg: 'bg-orange-500',
    description: t('home.categories.foodBeveragesDesc'),
    stats: t('home.categories.productsCount', { count: '30+' }),
    image: 'https://images.unsplash.com/photo-1606923829579-0cb981a83e2e?w=600&h=400&fit=crop'
  },
  { 
    id: 'vehicles', 
    name: t('home.categories.vehicles') || 'Vehicles', 
    icon: Car, 
    gradient: 'from-violet-500 to-purple-600',
    iconBg: 'bg-violet-500',
    description: t('home.categories.vehiclesDesc') || 'African-made vehicles and automotive parts',
    stats: t('home.categories.productsCount', { count: '20+' }),
    image: 'https://images.pexels.com/photos/3729464/pexels-photo-3729464.jpeg?w=600&h=400&fit=crop'
  },
];

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
};

export const CategoriesSection = () => {
  const { t } = useTranslation();
  const categories = getCategoriesData(t);
  
  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="categories-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-[#1E3A8A]/5 text-[#1E3A8A] px-4 py-2 rounded-full text-sm font-semibold mb-5">
            <Sparkles className="w-4 h-4" />
            {t('home.categories.explore')}
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl text-[#0A0A0A] tracking-tight mb-4">
            {t('home.categories.title')}
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            {t('home.categories.subtitle')}
          </p>
        </motion.div>

        {/* Bento Grid Layout - 8 categories */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5">
          {/* First Large Card - Agricultural */}
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.5, delay: 0 }}
            className="col-span-2 row-span-2"
          >
            <Link 
              to={`/products?category=${categories[0].id}`}
              className="group block h-full relative overflow-hidden rounded-3xl min-h-[320px]"
              data-testid="category-agricultural"
            >
              <img 
                src={categories[0].image} 
                alt={categories[0].name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className={`absolute inset-0 bg-gradient-to-t ${categories[0].gradient} opacity-75 group-hover:opacity-85 transition-opacity`} />
              
              <div className="absolute inset-0 p-6 flex flex-col justify-between">
                <div className="flex items-start justify-between">
                  <div className={`w-12 h-12 ${categories[0].iconBg} rounded-2xl flex items-center justify-center shadow-lg`}>
                    <Wheat className="w-6 h-6 text-white" />
                  </div>
                  <span className="bg-white/20 backdrop-blur-sm text-white text-sm font-medium px-4 py-2 rounded-full">
                    {categories[0].stats}
                  </span>
                </div>
                
                <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                    {categories[0].name}
                  </h3>
                  <p className="text-white/80 text-sm sm:text-base mb-3">{categories[0].description}</p>
                  <div className="flex items-center gap-2 text-white font-medium group-hover:gap-4 transition-all text-sm">
                    {t('home.categories.exploreProducts')} <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Remaining Category Cards */}
          {categories.slice(1).map((cat, idx) => {
            const IconComponent = cat.icon;
            return (
              <motion.div
                key={cat.id}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ duration: 0.5, delay: (idx + 1) * 0.05 }}
              >
                <Link 
                  to={`/products?category=${cat.id}`}
                  className="group block relative overflow-hidden rounded-2xl h-[160px]"
                  data-testid={`category-${cat.id}`}
                >
                  <img 
                    src={cat.image} 
                    alt={cat.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${cat.gradient} opacity-75 group-hover:opacity-85 transition-opacity`} />
                  
                  <div className="absolute inset-0 p-4 flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <div className={`w-9 h-9 ${cat.iconBg} rounded-xl flex items-center justify-center shadow-lg`}>
                        <IconComponent className="w-4 h-4 text-white" />
                      </div>
                      <span className="bg-white/20 backdrop-blur-sm text-white text-[10px] font-medium px-2 py-1 rounded-full">
                        {cat.stats}
                      </span>
                    </div>
                    
                    <div>
                      <h3 className="text-base font-bold text-white mb-0.5 leading-tight">
                        {cat.name}
                      </h3>
                      <p className="text-white/70 text-xs line-clamp-1">{cat.description}</p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* View All Button */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center mt-10"
        >
          <Link 
            to="/categories"
            className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#1a1a1a] transition-colors"
            data-testid="view-all-categories-btn"
          >
            {t('home.categories.viewAll')} <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
